import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CopyIcon, PencilIcon, ShareIcon, Trash2Icon } from "lucide-react";
import { createList, deleteList, UserContext, useFreshLists } from "../services/firebase";
import { useQueryClient } from "@tanstack/react-query";
import { asPlatforms } from "../shared/platforms";
import { List } from "../shared/types";

import Layout from "../components/Layout";
import { LogoIcon } from "../components/LogoIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/toast";
import { Heading } from "@/components/typography";

export default function Lists() {
    const user = useContext(UserContext);
    const { uid } = useParams<{ uid: string }>();
    const navigate = useNavigate();
    const [deleteModalId, setDeleteModalId] = useState('');

    const listsQuery = useFreshLists(uid ? { uid } : { creatorRequest: true });
    const lists = listsQuery.flatData;
    const listsLoading = listsQuery.query.isLoading;

    const queryClient = useQueryClient();

    async function handleAdd() {
        const newList = {
            name: "New List",
            // talentIds: [],
            talent: {},
            uid
        };

        const res = await createList(newList);

        if (res.success) {
            console.log(res);

            navigate(`/edit-list/${res.result}`);
        } else {
            alert("An error occured, please try again");
            console.log(res.error);
        }
    }

    async function handleShare(listId: string) {
        const listUrl = `https://app.grail-talent.com/new-booking/${listId}`;
        await navigator.clipboard.writeText(listUrl);
        alert(`Your list link has been copied to the clipboard:\n ${listUrl}`);
    }

    async function handleSharePublic(listId: string) {
        const listUrl = `https://app.grail-talent.com/public-list/${listId}`;
        await navigator.clipboard.writeText(listUrl);
        alert(`Your PUBLIC list link has been copied to the clipboard:\n ${listUrl}`);
    }

    function handleEdit(listId: string) {
        navigate(`/edit-list/${listId}`);
    }

    async function handleDuplicate(list: List) {
        const newList = {
            name: list.name + " (Copy)",
            talentIds: list.talentIds || [],
            platform: list.platform ?? null,
            uid: list.uid,
        }

        const res = await createList(newList);

        if (res.success) {
            console.log(res);
            navigate(`/edit-list/${res.result}`);
        } else {
            alert("An error occured, please try again");
            console.log(res.error);
        }
    }

    async function handleDelete(listId: string) {
        const list = lists.find((item) => item.id === listId);
        if (!canDeleteList(list)) {
            return;
        }

        const res = await deleteList(listId);

        if (res.success) {
            console.log(res);
            queryClient.removeQueries({ queryKey: ['lists', undefined, uid, uid ? undefined : true], exact: true });
            setDeleteModalId('');
        } else {
            console.log(res);
            toast.add({
                type: "error",
                title: "Couldn’t delete list",
                description: getMessageFromError(res.error),
            });
        }
    }

    function canDeleteList(list: List | undefined) {
        return !!(list && (list.uid === user?.id || user?.roles?.admin));
    }

    const listToDelete = lists.find((list) => list.id === deleteModalId);
    const phase = useSkeletonPhases(listsLoading);

    return (
        <Layout gray={true}>
            <Dialog open={!!deleteModalId} onOpenChange={(open) => { if (!open) setDeleteModalId(''); }}>
                <DialogContent className="sm:max-w-md" showCloseButton={false}>
                    <DialogHeader>
                        <DialogTitle render={<Heading type="h3" tag="h2" />}>
                            Are You Sure You Want to Delete?
                        </DialogTitle>
                        <DialogDescription className="space-y-4 text-foreground">
                            <span className="block">
                                Are you sure you want to delete the <em>{listToDelete?.name || deleteModalId}</em> list?
                            </span>
                            <span className="block">
                                This will permanently delete the list and <strong>can NOT be undone.</strong> The list link will stop working for any bookers who have it.
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    {/* Side-by-side action buttons */}
                    <DialogFooter className="sm:justify-stretch">
                        <Button
                            variant="destructive"
                            className="flex-1"
                            disabled={!canDeleteList(listToDelete)}
                            onClick={() => handleDelete(deleteModalId)}
                        >
                            Delete List
                        </Button>
                        <Button variant="secondary" className="flex-1" onClick={() => setDeleteModalId('')}>
                            Back to Safety
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="p-4">
                {/* Keep the page title and primary action on the same row. */}
                <div className="mb-4 flex items-center justify-between gap-4">
                    <Heading type="h1">Lists</Heading>
                    <Button onClick={handleAdd}>+ New List</Button>
                </div>
                {phase === "showSkeleton" ? (
                    Array.from({ length: 4 }, (_, index) => <ListRowSkeleton key={index} />)
                ) : phase === "showResult" && (lists?.length ? lists.map((list) =>
                    <Card size="sm" className="mb-2 gap-0 py-4" key={list.id}>
                        <CardContent className="flex items-center justify-between gap-4">
                            {/* Name + actions row */}
                            <div className="flex items-center gap-3">
                                {asPlatforms(list.platform).map((platform) => (
                                    <LogoIcon key={platform} brand={platform} className="size-6" title={platform} />
                                ))}
                                <div>
                                    <strong>{list.name}</strong>
                                    <div className="text-xs text-muted-foreground">
                                        {list.talentIds?.length || (list.talent && Object.keys(list.talent).length)} Creators
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap justify-end gap-1">
                                <Button variant="secondary" size="sm" aria-label="share" onClick={() => handleShare(list.id)}>
                                    <ShareIcon data-icon="inline-start" />
                                    Share
                                </Button>

                                <Button variant="secondary" size="sm" aria-label="share public" onClick={() => handleSharePublic(list.id)}>
                                    <ShareIcon data-icon="inline-start" />
                                    Share Public
                                </Button>

                                <Button variant="secondary" size="sm" aria-label="edit" onClick={() => handleEdit(list.id)}>
                                    <PencilIcon data-icon="inline-start" />
                                    Edit
                                </Button>

                                <Button variant="secondary" size="sm" aria-label="duplicate" onClick={() => handleDuplicate(list)}>
                                    <CopyIcon data-icon="inline-start" />
                                    Duplicate
                                </Button>

                                <Button
                                    variant="destructive"
                                    size="sm"
                                    aria-label="delete"
                                    disabled={!canDeleteList(list)}
                                    onClick={() => setDeleteModalId(list.id)}
                                >
                                    <Trash2Icon data-icon="inline-start" />
                                    Delete
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="px-4 py-16 text-center">
                        You don&apos;t have any lists yet... why not add a new one?
                    </div>
                ))}
            </div>
        </Layout>
    );
}

function getMessageFromError(error: unknown) {
    return error instanceof Error ? error.message : String(error);
}

/** Logo, name, and action-bar rhythm for a list-row wait. */
function ListRowSkeleton() {
    return (
        <Card size="sm" className="mb-2 gap-0 py-4">
            <CardContent className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Skeleton className="size-6" />
                    <Skeleton className="size-6" />
                    <div>
                        <Skeleton className="mb-1 h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                    </div>
                </div>
                <div className="flex flex-wrap justify-end gap-1">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-8 w-28" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-20" />
                </div>
            </CardContent>
        </Card>
    );
}
